'use client';

import SwapTextButton from '@/components/ui/SwapTextButton/SwapTextButton';
import { getCmsButtonLinkProps } from '@/lib/cms-button-target';
import { localizeUrl, pickButton, stripHtml } from '@/lib/homepage-mappers';
import type { PncPreFooter } from '@/types/people-and-culture';
import { TESTIMONIAL_COLORS } from '../lib/people-and-culture.constants';

interface JoinCtaSectionProps {
  locale: string;
  zone: PncPreFooter;
}

function isAccentColor(color: string | null | undefined): boolean {
  if (!color) return false;
  const normalized = color.trim().toUpperCase();
  return normalized === '#EC1C24' || normalized === '#ED1C22' || normalized === '#E31E24';
}

export default function JoinCtaSection({ locale, zone }: JoinCtaSectionProps) {
  const primaryButton =
    pickButton(zone.buttonDark, 'Solid') ?? zone.buttonDark?.[0] ?? zone.buttonLight?.[0];
  const secondaryButton =
    pickButton(zone.buttonLight, 'Solid') ?? zone.buttonLight?.[0] ?? zone.buttonDark?.[1];

  const initials = zone.textWithInitials?.initials ?? [];

  return (
    <section className="bg-white px-4 sm:px-6 md:px-16 lg:px-24">
      <div
        data-reveal
        className="flex flex-col gap-6 sm:gap-8 lg:flex-row lg:items-center lg:justify-between"
      >
        <div data-cta-left className="max-w-lg">
          <h2 className="text-3xl font-bold leading-tight text-[#0a0a0a] sm:text-4xl md:text-5xl">
            {zone.heading?.length
              ? zone.heading.map((part, index) => (
                  <span key={part.id ?? index}>
                    {index > 0 ? ' ' : ''}
                    <span
                      style={{
                        color: isAccentColor(part.textColor)
                          ? '#ed1c22'
                          : part.textColor || '#0a0a0a',
                      }}
                    >
                      {part.text}
                    </span>
                  </span>
                ))
              : zone.sectionTitle}
          </h2>
          {zone.subHeading ? (
            <p className="mt-4 text-sm leading-relaxed text-gray-500 sm:mt-5">
              {stripHtml(zone.subHeading)}
            </p>
          ) : null}
        </div>

        <div data-cta-right className="flex w-full flex-col gap-3 lg:w-[400px] lg:shrink-0">
          {primaryButton?.buttonText?.trim() ? (
            <SwapTextButton
              text={primaryButton.buttonText.trim()}
              hoverText={primaryButton.buttonText.trim()}
              aria-label={primaryButton.ariaLabel?.trim() || primaryButton.buttonText.trim()}
              href={localizeUrl(primaryButton.buttonUrl, locale) ?? undefined}
              {...getCmsButtonLinkProps(primaryButton)}
              className="w-full justify-center whitespace-nowrap !rounded-xl !px-6 !py-4 shadow-[0_4px_20px_rgba(237,28,34,0.35)]"
            />
          ) : null}

          {secondaryButton?.buttonText?.trim() ? (
            <SwapTextButton
              text={secondaryButton.buttonText.trim()}
              hoverText={secondaryButton.buttonText.trim()}
              aria-label={secondaryButton.ariaLabel?.trim() || secondaryButton.buttonText.trim()}
              variant="outline"
              href={localizeUrl(secondaryButton.buttonUrl, locale) ?? undefined}
              {...getCmsButtonLinkProps(secondaryButton)}
              className="w-full whitespace-nowrap justify-center !rounded-xl !px-6 !py-4 !bg-gray-50 !text-[#0a0a0a] !border-gray-200 hover:!bg-gray-100"
            />
          ) : null}

          {zone.textWithInitials?.text || initials.length > 0 ? (
            <div className="mt-1 flex items-center gap-2.5 px-1 sm:mt-2">
              {initials.length > 0 ? (
                <div className="flex -space-x-2">
                  {initials.map((item, idx) => (
                    <div
                      key={item.id}
                      className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[9px] font-bold text-white"
                      style={{
                        backgroundColor: TESTIMONIAL_COLORS[idx % TESTIMONIAL_COLORS.length],
                      }}
                    >
                      {item.listTitle}
                    </div>
                  ))}
                </div>
              ) : null}
              {zone.textWithInitials?.text ? (
                <p className="text-xs text-gray-400">{zone.textWithInitials.text}</p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
